import { boot } from "@origin-digital/bootloader";
import { configFetch } from "@origin-digital/daxi";
import { initHandler } from "@origin-digital/mesh";
import { initWebChat } from "@origin-digital/origin-chat";
import { EnvironmentNames } from "@origin-digital/platform-enums";
import { isNative as verifyIsNative } from "@origin-digital/platform-helpers";
import { configAnalytics } from "@origin-digital/reporting-client";
import { parseQueryParamsWithNativeGate } from "@origin-digital/native-features";
import { initGeoTargeting } from "@origin-digital/geo-targeting";
import {
  isChatClientSdkEnabled,
  isUnauthInternetChatEnabled,
} from "@origin-digital/chat-client-sdk-launcher/chatClientAvailability";
import {
  initializeUnauthInternetChat,
  isUnauthInternetChatPath,
} from "@origin-digital/chat-client-sdk-launcher/unauth/internet/unauthInternetChat";
import { FEATURE_FLAGS, getDefaultFeatures } from "./features";
import { shouldInitialize } from "./utils";

// Override webpack import path to support tal-client build. Used from globals by webpack
// eslint-disable-next-line @typescript-eslint/no-unused-vars
declare let __webpack_public_path__: string;

// Snapshot env once at boot. Freezing prevents post-bootstrap script
// injections (3P scripts, late XSS) from rewriting env to flip security
// gates at call time. Reads through `window.oetal.env` elsewhere will hit
// the frozen object.
if (typeof window !== "undefined" && window.oetal && window.oetal.env) {
  Object.freeze(window.oetal.env);
}

const environment = window.oetal.env.name as EnvironmentNames;

/* Use the talClientBuild to fetch assets for tal-client assets, rather than the deployed ones*/
function configureRuntimePublicPath(): void {
  if (typeof window === "undefined") {
    return;
  }

  // talClientBuild only used in staging
  if (environment !== "staging") {
    return;
  }
  const publicAssetsPath = window.oetal?.env?.talPublicAssetsPath;
  if (typeof publicAssetsPath !== "string") {
    return;
  }

  __webpack_public_path__ = publicAssetsPath.endsWith("/")
    ? publicAssetsPath
    : `${publicAssetsPath}/`;
}

configureRuntimePublicPath();

const isSSR = window.oetal.ssr.onServer;
const {
  allowedScopes = [],
  authenticated = false,
  enableChat,
  enableChatClientSdk: routeChatClientSdkEnabled,
  enableSurvicate = true,
} = window.oetal.pageData;

const featureFlags = getDefaultFeatures(environment);
const enableChatClientSdk = isChatClientSdkEnabled({
  featureEnabled: featureFlags[FEATURE_FLAGS.CHAT_CLIENT_SDK],
  routeEnabled: routeChatClientSdkEnabled,
});
const enableUnauthInternetChat = isUnauthInternetChatEnabled(
  featureFlags[FEATURE_FLAGS.UNAUTH_INTERNET_CHAT_CLIENT]
);

const disableAuthCheck = ["local", "dev"].includes(environment);
const isNative = verifyIsNative();

let path = "";
let parameters: Record<string, string> = {};
if (typeof window !== "undefined") {
  path = window.location.pathname;
  // Strip the native emulator query param in prod-like envs. This is the
  // single high-level gate: all downstream consumers (mesh, braze, boot)
  // only see parameters cleared by env.
  parameters = parseQueryParamsWithNativeGate(
    window.location.search,
    environment
  );
}

initHandler({
  isNative,
  environment,
  parameters,
  enableChatClientSdk,
});

if (enableUnauthInternetChat) {
  initializeUnauthInternetChat({ pathname: path, isSSR, isNative });
}

configFetch({
  environment: environment as any,
  disableAuthCheck,
});

if (
  enableChat &&
  !(enableUnauthInternetChat && isUnauthInternetChatPath(path))
) {
  initWebChat();
}

initGeoTargeting();

import("@origin-digital/braze").then((res) => {
  res.initBraze({
    environment,
    path,
    parameters,
  });
});

configAnalytics({
  debugToConsole: true,
});

if (enableSurvicate && shouldInitialize()) {
  import("@origin-digital/survicate").then((res) =>
    res.initSurvicate(environment)
  );
}

boot({
  isNative,
  authenticated,
  isSSR,
  path,
  parameters,
  environment,
  allowedScopes,
});
